import type { AddressShipmentOptional } from "../../types";
import { EMPTY, formatAddress } from "./readonlyFormat";

/**
 * The reading half of the container-orders module.
 *
 * A saved record is presented, not shown as its own form greyed out: a disabled
 * input still costs the reader a box, a border and a placeholder per value, and
 * gives the ISO code the same visual weight as the client. Here the data is the
 * layout — definition lists, and the route as three ordered stops.
 *
 * Shared by the order's read view and the container's, because a dispatcher
 * moves between the two all day and a value that is presented differently in the
 * two places is a value they have to re-read. The formatting helpers live in
 * `readonlyFormat.ts`.
 */

/**
 * One label/value pair.
 *
 * Values sit *under* their labels rather than beside them. The edit form uses
 * side-by-side rows because every row has an input of a known height; reading a
 * saved record is the opposite problem — the values vary wildly in length, and
 * stacking lets each one take the width it needs without dragging its
 * neighbours out of alignment.
 */
export function Field({
  label,
  value,
  mono = false,
  wide = false,
}: {
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
  wide?: boolean;
}) {
  const isEmpty =
    value === undefined || value === null || value === "" || value === EMPTY;

  return (
    // Container-query spans, matching FieldGrid's container-query columns —
    // viewport breakpoints here would disagree with the grid whenever the card
    // is narrower than the window.
    <div className={wide ? "@sm:col-span-2 @2xl:col-span-3" : undefined}>
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-mist">
        {label}
      </dt>
      <dd
        className={`mt-0.5 wrap-break-word text-[13px] ${
          isEmpty ? "text-secondary-300" : "text-ink"
        } ${mono ? "font-mono" : ""}`}
      >
        {isEmpty ? EMPTY : value}
      </dd>
    </div>
  );
}

/** A responsive definition grid — the layout every section body shares. */
export function FieldGrid({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-1 gap-x-4 gap-y-3 @sm:grid-cols-2 @2xl:grid-cols-3">
      {children}
    </dl>
  );
}

/**
 * One stop of a route, laid out for reading.
 *
 * The numbered badge and the name/address above the detail rows are what make
 * three of these read as a journey rather than three unrelated cards.
 */
export function ReadonlyStopCard({
  index,
  icon: Icon,
  title,
  kind,
  accent,
  companyName,
  address,
  rows,
  muted = false,
}: {
  index: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  kind: string;
  accent: string;
  companyName?: string;
  address?: AddressShipmentOptional | null;
  rows?: Array<{ label: string; value?: React.ReactNode }>;
  /**
   * A stop that is on the route but not being made.
   *
   * Struck through and dimmed rather than hidden, because the reader needs to
   * know it was considered: a route that simply ends after the loading site
   * reads as a missing stop, and "no drop-off — the box is staying on site"
   * reads as a decision. The reason is one of the rows.
   */
  muted?: boolean;
}) {
  return (
    <div
      className={`rounded-md border bg-white p-3 ${
        muted
          ? "border-dashed border-[rgba(11,15,14,0.18)] bg-bone/60"
          : "border-[rgba(11,15,14,0.08)]"
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${accent}`}
        >
          {index}
        </span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 ${muted ? "text-secondary-300" : "text-mist"}`}
        />
        <span
          className={`truncate text-[12px] font-semibold ${
            muted ? "text-mist line-through" : "text-ink"
          }`}
        >
          {title}
        </span>
        <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-mist">
          {kind}
        </span>
      </div>

      <p
        className={`text-[13px] font-semibold ${
          muted ? "text-mist" : "text-ink"
        }`}
      >
        {companyName || EMPTY}
      </p>
      <p className="mt-0.5 text-[12px] leading-snug text-secondary-600">
        {formatAddress(address) || EMPTY}
      </p>

      {!!rows?.length && (
        <dl className="mt-3 space-y-2 border-t border-[rgba(11,15,14,0.06)] pt-2">
          {rows.map((row) => (
            <div key={row.label} className="flex items-baseline gap-2">
              <dt className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-mist">
                {row.label}
              </dt>
              <dd
                className={`ml-auto wrap-break-word text-right text-[12px] ${
                  row.value ? "text-ink" : "text-secondary-300"
                }`}
              >
                {row.value || EMPTY}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
